import { useState, useMemo } from 'react';
import type { Order } from '@/types/index';

export const useOrderFiltering = (orders: Order[]) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dateRange, setDateRange] = useState<'today' | 'yesterday' | 'week' | 'all'>('today');

    const filteredOrders = useMemo(() => {
        let result = [...orders];

        // 1. Filter by Date Range
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);

        if (dateRange === 'today') {
            result = result.filter(o => {
                if (!o.created_at) return false;
                const d = new Date(o.created_at);
                return d >= today;
            });
        } else if (dateRange === 'yesterday') {
            result = result.filter(o => {
                if (!o.created_at) return false;
                const d = new Date(o.created_at);
                return d >= yesterday && d < today;
            });
        } else if (dateRange === 'week') {
            result = result.filter(o => {
                if (!o.created_at) return false;
                const d = new Date(o.created_at);
                return d >= weekAgo;
            });
        }

        // 2. Filter by Status
        if (statusFilter !== 'all') {
            result = result.filter(o => o.status === statusFilter);
        }

        // 3. Filter by Search Term
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(o =>
                o.order_number.toLowerCase().includes(lowerTerm) ||
                (o.customer_name && o.customer_name.toLowerCase().includes(lowerTerm)) ||
                (o.table_number && o.table_number.toLowerCase().includes(lowerTerm))
            );
        }

        return result;
    }, [orders, searchTerm, statusFilter, dateRange]);

    const orderStats = useMemo(() => {
        const stats = {
            total: filteredOrders.length,
            pending: 0,
            preparing: 0,
            ready: 0,
            completed: 0,
            revenue: 0
        };

        filteredOrders.forEach(order => {
            if (order.status === 'pending') stats.pending++;
            else if (order.status === 'preparing') stats.preparing++;
            else if (order.status === 'ready') stats.ready++;
            else if (order.status === 'completed') {
                stats.completed++;
                stats.revenue += order.total_amount;
            }
        });

        return stats;
    }, [filteredOrders]);

    return {
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        dateRange,
        setDateRange,
        filteredOrders,
        orderStats
    };
};
